import { RtcLink, type RtcOptions } from '../webrtc/rtcLink';
import { RelayLink } from '../relayLink';
import type { Link } from '../link';
import { currentPass } from '../gate';

/** How long a newcomer waits for a direct connection before relaying through the server instead. */
export const DIRECT_TIMEOUT_MS = 8000;
/** How often a lost peer is retried, and how soon a dropped server connection is reopened. */
const RETRY_MS = 5000;
const REOPEN_MS = 3000;

/**
 * WebSocket signalling client for the online lobby service (worker/index.ts) and the optional local
 * signalling service (tools/signalling-server.mjs). The server only relays session descriptions
 * and ICE candidates between peers in a room; gameplay never flows through it.
 * Newer peers initiate connections to every existing peer (full mesh). If a direct connection has
 * not opened within DIRECT_TIMEOUT_MS, or fails, the newcomer switches that peer to a RelayLink
 * through the same WebSocket, and the other side follows when the first relayed message arrives.
 *
 * During a game it stays connected so lost peers can be reconnected (see reconnect): if the server
 * connection itself drops, it is reopened and the room rejoined, and a rejoining peer offers a new
 * link to everyone it has lost.
 */
export class WsSignalling {
  private ws!: WebSocket;
  private links = new Map<string, RtcLink>();
  private relays = new Map<string, RelayLink>();
  /** Relays we announced that the other side has not answered yet: not usable until it does. */
  private relayPending = new Set<string>();
  private earlyIce = new Map<string, RTCIceCandidateInit[]>();
  /** Peers we created an offer for that has not opened yet (to settle simultaneous offers). */
  private initiating = new Set<string>();
  /** Peers to reconnect to, and the retry timer. */
  private wanted = new Set<string>();
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  private reopenTimer: ReturnType<typeof setTimeout> | null = null;
  private joined = false;
  private closedByUs = false;
  private released = false;
  onLink: ((l: Link) => void) | null = null;
  /** A peer is being reached through the server rather than directly. */
  onRelay: ((id: string) => void) | null = null;
  onError: ((e: string) => void) | null = null;
  onPeers: ((ids: string[]) => void) | null = null;

  /** `create` marks the host: the online lobby service refuses joiners to a room with no host. */
  constructor(private url: string, readonly room: string, readonly localId: string, private opts: RtcOptions, private create = false) {
    this.connect();
  }

  private connect(): void {
    // Carry the start-up pass; the server refuses connections without one.
    const url = this.url;
    this.ws = new WebSocket(url + (url.includes('?') ? '&' : '?') + 'p=' + encodeURIComponent(currentPass() ?? ''));
    // A second join (after the connection dropped) is a rejoin: allowed even if the room is empty.
    this.ws.onopen = () => {
      this.ws.send(JSON.stringify({ type: 'join', room: this.room, id: this.localId, create: this.create && !this.joined, rejoin: this.joined }));
      this.joined = true;
    };
    this.ws.onerror = () => {
      if (!this.joined) this.onError?.(`Could not reach the signalling service at ${url}`);
    };
    this.ws.onmessage = (e) => void this.handle(JSON.parse(String(e.data)));
    this.ws.onclose = () => {
      for (const r of [...this.relays.values()]) r.close();
      // Dropped unexpectedly after joining (the network went away): reopen and rejoin.
      if (this.joined && !this.closedByUs && !this.released) {
        if (this.reopenTimer) clearTimeout(this.reopenTimer);
        this.reopenTimer = setTimeout(() => {
          this.reopenTimer = null;
          if (!this.closedByUs && !this.released) this.connect();
        }, REOPEN_MS);
      }
    };
  }

  /** Whether any peer is reached through the server, so the WebSocket must stay open. */
  get relaying(): boolean {
    return this.relays.size > 0;
  }

  /** Signalling is no longer needed: close now, or once the last relayed peer has gone. */
  release(): void {
    this.released = true;
    if (!this.relaying) this.close();
  }

  private isOpen(id: string): boolean {
    return !!this.links.get(id)?.open || (this.relays.has(id) && !this.relayPending.has(id));
  }

  /**
   * Try to re-establish a lost link to `peer` until it opens (or `stop` / close). Of the two sides,
   * the one with the lower id makes the offer; the other nudges it through the server, in case it
   * has not noticed the loss yet. A rejoining peer offers to everyone itself (see 'peers').
   */
  reconnect(peer: string): void {
    if (this.closedByUs) return;
    this.wanted.add(peer);
    this.retry();
    this.retryTimer ??= setInterval(() => this.retry(), RETRY_MS);
  }

  /** Stop trying to reach `peer` (it came back, or the game carried on without it). */
  stop(peer: string): void {
    this.wanted.delete(peer);
    if (!this.wanted.size && this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private retry(): void {
    for (const p of [...this.wanted]) {
      if (this.isOpen(p)) {
        this.stop(p);
        continue;
      }
      if (this.ws.readyState !== WebSocket.OPEN) continue;
      // An unanswered relay from the last attempt: drop it and try afresh.
      if (this.relayPending.has(p)) this.dropPendingRelay(p);
      if (this.localId < p) void this.offer(p);
      else this.send({ type: 'signal', room: this.room, from: this.localId, to: p });
    }
  }

  private async offer(p: string): Promise<void> {
    if (this.initiating.has(p)) return;
    this.initiating.add(p);
    const l = this.makeLink(p, true);
    const offer = await l.createOffer();
    this.send({ type: 'signal', room: this.room, from: this.localId, to: p, sdp: offer });
  }

  private send(m: unknown): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  private makeLink(remote: string, initiator: boolean): RtcLink {
    const l = new RtcLink(remote, this.opts, initiator);
    l.onIce = (c) => this.send({ type: 'signal', room: this.room, from: this.localId, to: remote, ice: c });
    this.links.set(remote, l);
    void l.opened.then(() => {
      this.initiating.delete(remote);
      if (this.links.get(remote) === l) this.onLink?.(l);
    });
    if (initiator) {
      // Until the session takes the link over, a failure means: try the relay instead.
      l.onClose = () => {
        this.initiating.delete(remote);
        this.useRelay(remote, true);
      };
      setTimeout(() => {
        this.initiating.delete(remote);
        if (!l.open && this.links.get(remote) === l) this.useRelay(remote, true);
      }, DIRECT_TIMEOUT_MS);
    }
    return l;
  }

  private dropPendingRelay(p: string): void {
    const r = this.relays.get(p);
    this.relayPending.delete(p);
    this.relays.delete(p);
    if (r) {
      r.onClose = null;
      r.close();
    }
  }

  /**
   * Reach `remote` through the server from now on; `announce` tells the other side to do the same.
   * An announced relay is handed over only once the other side answers through it, so a peer that
   * has gone is never mistaken for one reached by relay. The answering side replies at once.
   */
  private useRelay(remote: string, announce: boolean): RelayLink | null {
    const have = this.relays.get(remote);
    if (have) {
      if (!announce && this.relayPending.delete(remote)) {
        this.onRelay?.(remote);
        this.onLink?.(have);
      }
      return have;
    }
    if (this.ws.readyState !== WebSocket.OPEN) return null;
    const rtc = this.links.get(remote);
    if (announce && rtc?.open) return null;
    this.links.delete(remote);
    if (rtc) {
      rtc.onClose = null;
      rtc.onMessage = null;
      rtc.close();
    }
    const r = new RelayLink(
      remote,
      (data) => this.send({ type: 'relay', room: this.room, to: remote, data }),
      () => {
        this.relays.delete(remote);
        this.relayPending.delete(remote);
        if (this.released && !this.relaying) this.close();
      },
    );
    this.relays.set(remote, r);
    this.send({ type: 'relay', room: this.room, to: remote, data: '' });
    if (announce) {
      this.relayPending.add(remote);
      return r;
    }
    this.onRelay?.(remote);
    this.onLink?.(r);
    return r;
  }

  private async handle(m: { type: string; peers?: string[]; from?: string; id?: string; data?: string; sdp?: RTCSessionDescriptionInit; ice?: RTCIceCandidateInit; reason?: string }): Promise<void> {
    if (m.type === 'relay' && m.from) {
      const r = this.useRelay(m.from, false);
      if (r && m.data) r.receive(m.data);
    } else if (m.type === 'left' && m.id) {
      this.relays.get(m.id)?.close();
    } else if (m.type === 'error') {
      this.onError?.(m.reason ?? 'The signalling service refused the connection.');
    } else if (m.type === 'peers' && m.peers) {
      this.onPeers?.(m.peers);
      // We are the newcomer (or rejoining): offer to everyone present we are not linked to.
      for (const p of m.peers) {
        if (p === this.localId || this.isOpen(p)) continue;
        void this.offer(p);
      }
    } else if (m.type === 'signal' && m.from) {
      if (this.relays.has(m.from)) return;
      // A nudge (no description, no candidate): the other side lost us and wants an offer. Our own
      // link may still look open (the break not noticed yet): it is dead, so close it and offer.
      if (!m.sdp && !m.ice) {
        const stale = this.links.get(m.from);
        if (stale?.open) stale.close();
        this.relays.get(m.from)?.close();
        this.reconnect(m.from);
        return;
      }
      let l = this.links.get(m.from);
      if (m.sdp?.type === 'offer') {
        // Both offered at once: the lower id's offer wins.
        if (this.initiating.has(m.from) && this.localId < m.from) return;
        this.initiating.delete(m.from);
        const early = this.earlyIce.get(m.from) ?? [];
        this.earlyIce.delete(m.from);
        l = this.makeLink(m.from, false);
        for (const c of early) void l.addIce(c);
        const ans = await l.acceptOffer(m.sdp);
        this.send({ type: 'signal', room: this.room, from: this.localId, to: m.from, sdp: ans });
      } else if (m.sdp?.type === 'answer' && l) await l.acceptAnswer(m.sdp);
      else if (m.ice && l) await l.addIce(m.ice);
      else if (m.ice) (this.earlyIce.get(m.from) ?? this.earlyIce.set(m.from, []).get(m.from)!).push(m.ice);
    }
  }

  close(): void {
    this.closedByUs = true;
    if (this.retryTimer) clearInterval(this.retryTimer);
    if (this.reopenTimer) clearTimeout(this.reopenTimer);
    this.retryTimer = null;
    this.wanted.clear();
    try {
      this.ws.close();
    } catch {
      /* ignore */
    }
  }
}
